import {
  Bell,
  Building2,
  Cable,
  ClipboardList,
  Cog,
  HeartPulse,
  KeyRound,
  Landmark,
  MessageSquareReply,
  Receipt,
  Repeat,
  SlidersHorizontal,
  Sparkles,
  UserRound,
} from "lucide-react";
import { ROUTES, routeUrl } from "@/app/routes";
import { type AccountRole, Permission, type WorkspaceRole } from "@/shared/auth/permissions";
import type { CommandEntry } from "@/shared/components/command-menu/command-match";
import { NAV_SECTIONS, type NavItem } from "@/shared/components/layout/sidebar-nav.config";
import { keepItem } from "@/shared/components/layout/sidebar-nav.gate";
import type { Workspace } from "@/shared/components/layout/sidebar-workspace.constants";
import type { Entitlements } from "@/shared/entitlements/schemas/entitlements.schema";

/// Destinations that exist as routes but not as sidebar entries. Declared in
/// the same NavItem shape as the sidebar so they run through the identical
/// `keepItem` gate: one truth table, no second answer to "who sees what".
/// Each `requires` mirrors the destination route's own `useCanDo` check, so a
/// command can never lead somewhere the route then refuses to render.
export const COMMAND_EXTRA_ITEMS: NavItem[] = [
  {
    to: ROUTES.workspace.settings.path,
    label: "Workspace settings",
    icon: Cog,
    requires: Permission.WORKSPACE_EDIT_SETTINGS,
  },
  {
    to: ROUTES.workspace.settingsCompany.path,
    label: "Company details",
    icon: Landmark,
    requires: Permission.WORKSPACE_EDIT_SETTINGS,
  },
  {
    to: ROUTES.workspace.healthConfig.path,
    label: "Health config",
    icon: HeartPulse,
    requires: Permission.WORKSPACE_HEALTH_CONFIG_EDIT,
  },
  { to: ROUTES.workspace.notifications.path, label: "Notifications", icon: Bell },
  {
    to: ROUTES.workspace.notificationsPreferences.path,
    label: "Notification preferences",
    icon: SlidersHorizontal,
  },
  { to: ROUTES.settingsProfile.path, label: "My profile", icon: UserRound },
  // Distinct from "My profile" on purpose: /profile is now only the reviews
  // released to you, and labelling it "profile" sent people looking for
  // their avatar or password to a page that has neither.
  { to: ROUTES.profile.path, label: "My reviews", icon: UserRound },
  { to: ROUTES.settingsAccount.path, label: "Account settings", icon: Building2 },
  // Same labels and icons as the settings strip (settings-nav.ts), so a
  // search result and the tab it lands on read as one place.
  { to: ROUTES.settingsTokens.path, label: "API tokens", icon: KeyRound },
  { to: ROUTES.settingsMcp.path, label: "MCP", icon: Sparkles },
  {
    to: ROUTES.settingsAiConnections.path,
    label: "MCP connections",
    icon: Cable,
    requires: Permission.ACCOUNT_MANAGE_AI_CONNECTIONS,
  },
  // Fix (final review, MINOR): this had no command-menu entry at all, so
  // Cmd+K could not reach `/account/billing` no matter what the buyer typed;
  // the "billing"/"plan" keywords below used to sit on Account settings
  // instead, misdirecting a search for either word. No `requires`, same as
  // Account settings above: IMPORTANT 1 opened this route's plan half to
  // every authenticated user, not just the owner.
  { to: ROUTES.accountBilling.path, label: "Billing", icon: Receipt },
  {
    to: ROUTES.workspace.adminTodos.path,
    label: "All todos",
    icon: ClipboardList,
    requires: Permission.TODO_VIEW_WORKSPACE,
  },
  {
    to: ROUTES.workspace.adminFeedback.path,
    label: "All feedback",
    icon: MessageSquareReply,
    requires: Permission.FEEDBACK_VIEW_WORKSPACE,
  },
];

/// Search synonyms, keyed by the templated route path. Nav labels are one or
/// two words, so without these "analytics" finds nothing and the menu reads as
/// broken to anyone who does not already speak our vocabulary.
export const COMMAND_KEYWORDS: Record<string, string[]> = {
  [ROUTES.workspace.home.path]: ["dashboard", "start", "overview"],
  [ROUTES.workspace.insights.path]: ["analytics", "charts", "reports", "trends"],
  [ROUTES.workspace.people.path]: ["team", "employees", "directory", "colleagues", "staff"],
  [ROUTES.workspace.orgChart.path]: ["hierarchy", "reporting line", "structure"],
  [ROUTES.workspace.myTeam.path]: ["reports", "direct reports"],
  [ROUTES.workspace.reviews.path]: ["performance", "appraisal", "evaluation"],
  [ROUTES.workspace.reviewsTemplates.path]: ["questions", "template", "question set"],
  [ROUTES.workspace.oneOnOnes.path]: ["1:1", "one on one", "meeting", "check in"],
  [ROUTES.workspace.kudos.path]: ["praise", "recognition", "shout out"],
  [ROUTES.workspace.feedback.path]: ["comments", "suggestions"],
  [ROUTES.workspace.goals.path]: ["okr", "objectives", "targets"],
  [ROUTES.workspace.todos.path]: ["tasks", "action items"],
  [ROUTES.workspace.myPulse.path]: ["mood", "survey", "check in", "wellbeing", "pulse", "my pulse"],
  [ROUTES.workspace.pulseSurveys.path]: ["survey", "questionnaire", "engagement", "pulse"],
  [ROUTES.workspace.expenses.path]: ["expenses", "claims", "reimbursement"],
  [ROUTES.workspace.recruitment.path]: ["recruitment", "hiring", "jobs", "ats"],
  [ROUTES.workspace.myActions.path]: ["follow ups", "next steps"],
  // Payroll had NO keyword entries at all, which is why searching the command
  // menu for "payroll" or "run payroll" returned nothing even once the sidebar
  // rows existed: the labels are "Run payroll", "My payslips" and "Setup", and
  // "Setup" alone matches nothing anybody would type. These are the words an
  // HR manager actually reaches for on payday.
  [ROUTES.workspace.payrollRuns.path]: [
    "payroll",
    "run payroll",
    "pay",
    "payday",
    "pay run",
    "process payroll",
    "salary",
    "pay my team",
    "bank file",
    "bank export",
    "payment file",
  ],
  [ROUTES.workspace.myPayslips.path]: [
    "payslip",
    "pay slip",
    "salary slip",
    "my salary",
    "my pay",
    "payroll",
  ],
  // ESS-01. Most of these are what somebody types when they are trying to fix
  // a problem rather than browse a page: "bank details" after a failed
  // transfer, "80c" while filling in a declaration, "pan" when HR has asked
  // for it. None of them matches the label "My pay details".
  [ROUTES.workspace.myPayDetails.path]: [
    "bank details",
    "bank account",
    "pan",
    "uan",
    "esi",
    "pf",
    "tax declaration",
    "tax regime",
    "80c",
    "investment proof",
    "my pay details",
  ],
  [ROUTES.workspace.payrollSetup.path]: [
    "payroll setup",
    "salary structure",
    "pay heads",
    "salary components",
    "tax slab",
    "payroll configuration",
    "ctc",
    "pf",
    "esi",
  ],
  [ROUTES.workspace.payrollSalaries.path]: ["ctc", "annual ctc", "salary", "compensation"],
  [ROUTES.workspace.payrollInputs.path]: [
    "payroll inputs",
    "this month's extras",
    "this months extras",
    "extras",
    "bonus",
    "arrears",
    "override",
    "additional salary",
  ],
  [ROUTES.workspace.recoverySchedules.path]: [
    "recoveries",
    "arrears",
    "advance",
    "loan recovery",
    "deduction",
  ],
  [ROUTES.workspace.settlements.path]: [
    "f&f",
    "fnf",
    "full and final",
    "exit",
    "settlement",
    "leaver",
  ],
  [ROUTES.workspace.complianceDocuments.path]: [
    "form 16",
    "form16",
    "compliance",
    "statutory",
    "tds certificate",
    "year-end",
  ],
  [ROUTES.workspace.variablePay.path]: ["bonus", "incentive", "variable pay"],
  [ROUTES.workspace.parallelRun.path]: ["parallel run", "migration", "cutover", "legacy register"],
  [ROUTES.workspace.payrollMoneyAudit.path]: ["money audit", "audit", "pay history"],
  [ROUTES.workspace.taxDeclarations.path]: [
    "tax declarations",
    "tax calculator",
    "80c",
    "pf",
    "esi",
  ],
  [ROUTES.workspace.benefits.path]: ["benefits", "flexi", "perks", "reimbursement"],
  [ROUTES.workspace.settings.path]: ["configuration", "preferences", "admin"],
  [ROUTES.workspace.settingsCompany.path]: [
    "company details",
    "logo",
    "legal name",
    "letterhead",
    "gstin",
    "cin",
  ],
  [ROUTES.accountSettings.path]: ["organisation", "organization"],
  // Fix (final review, MINOR): "billing" and "plan" used to sit on Account
  // settings above, which routes searches for either word to the WRONG
  // destination now that this page exists.
  [ROUTES.accountBilling.path]: [
    "billing",
    "plan",
    "payment",
    "purchase",
    "subscription",
    "upgrade",
    "lifetime deal",
  ],
  [ROUTES.workspace.notifications.path]: ["alerts", "inbox"],
  [ROUTES.settingsProfile.path]: ["me", "my account", "avatar", "password"],
  [ROUTES.settingsTokens.path]: ["api key", "api keys", "token", "personal access token"],
  // "MCP" means nothing to most people; they search for the tool they use.
  [ROUTES.settingsMcp.path]: [
    "ai tools",
    "claude",
    "claude code",
    "chatgpt",
    "openai",
    "codex",
    "cursor",
    "connect",
    "connector",
    "model context protocol",
  ],
  [ROUTES.settingsAiConnections.path]: ["ai connections", "connected apps", "claude", "chatgpt"],
  [ROUTES.profile.path]: ["my results", "released reviews"],
};

/// Collapsible parents carry `to: ""` and exist only to group children in the
/// sidebar. The command menu is a flat list, so parents are dropped and their
/// children promoted.
export function flattenNavItems(items: NavItem[]): NavItem[] {
  return items.flatMap((item) => {
    if (item.heading) return [];
    if (item.children) return flattenNavItems(item.children);
    return item.to === "" ? [] : [item];
  });
}

export function buildNavCommands({
  accountRole,
  workspaceRoles,
  features,
  workspaceSlug,
}: {
  accountRole: AccountRole | null;
  workspaceRoles: WorkspaceRole[];
  features: Entitlements["features"];
  workspaceSlug: string;
}): CommandEntry[] {
  const sidebarItems = NAV_SECTIONS.flatMap((section) => section.items);
  const gated = [...sidebarItems, ...COMMAND_EXTRA_ITEMS]
    .map((item) => keepItem(item, accountRole, workspaceRoles, features))
    .filter((item): item is NavItem => item !== null);

  return flattenNavItems(gated).map((item) => ({
    id: `nav:${item.to}`,
    kind: "nav" as const,
    label: item.label,
    // Same resolution the sidebar uses (sidebar-nav.tsx). The template came
    // from the route catalog, so audit rule RT1 stays satisfied.
    href: item.to.replace("$workspaceSlug", workspaceSlug),
    icon: item.icon,
    keywords: COMMAND_KEYWORDS[item.to],
  }));
}

/// Narrower than the full `Workspace` (which carries mark, gradient, plan, and
/// member count) because the command menu only needs identity and URL.
export type WorkspaceOption = Pick<Workspace, "id" | "slug" | "name">;

export function buildWorkspaceCommands({
  workspaces,
  activeWorkspaceId,
}: {
  workspaces: ReadonlyArray<WorkspaceOption>;
  activeWorkspaceId: string;
}): CommandEntry[] {
  return workspaces
    .filter((workspace) => workspace.id !== activeWorkspaceId)
    .map((workspace) => ({
      id: `workspace:${workspace.id}`,
      kind: "workspace" as const,
      label: workspace.name,
      href: routeUrl.workspace.home(workspace.slug),
      icon: Repeat,
      hint: "Switch workspace",
    }));
}

/// Exact pathname lookup, used to record a visit as a recent. Detail pages
/// (`/acme/goals/abc123`) intentionally match nothing: recording one would
/// mean inventing a label we do not have.
export function findCommandByHref(entries: CommandEntry[], pathname: string): CommandEntry | null {
  return entries.find((entry) => entry.href === pathname) ?? null;
}

/// Turns stored recents back into live commands. Two filters matter:
/// a recent whose destination the caller has since lost access to disappears
/// (it is no longer in `navCommands`), and the page you are already on is
/// dropped, since recording happens on navigation and the top recent would
/// otherwise always be "where you already are".
export function resolveRecentCommands(
  recents: ReadonlyArray<{ id: string }>,
  navCommands: CommandEntry[],
  currentPathname: string,
): CommandEntry[] {
  return recents
    .map((recent) => navCommands.find((entry) => entry.id === recent.id))
    .filter(
      (entry): entry is CommandEntry => entry !== undefined && entry.href !== currentPathname,
    );
}
